"use client";

import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import NextImage, { type StaticImageData } from "next/image";
import Image from "@/helpers/Image";
import { AsciiReveal, type Crop } from "./AsciiReveal";
import flavioPhoto from "@/public/home/testimonials/founder-flavio.webp";
import lionelPhoto from "@/public/home/testimonials/founder-lionel.webp";
import galaxyImage from "@/public/home/testimonials/galaxyImage.jpg";
import galaxyImage2 from "@/public/home/data-spec.png";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import Chip from "@/components/global/ui/Chip";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableText } from "next-sanity";

const EASE = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_CHIP = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TITLE = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const CHIP_TRANSITION = { duration: 0.7, ease: [...EASE] };
const TITLE_TRANSITION = { duration: 0.8, ease: [...EASE], delay: 0.08 };

type Layout = {
  left: number;
  top: number;
  width: number;
  height: number;
  mobileWidth?: number;
}; // all rem

type Testimonial = {
  kind: "testimonial";
  id: string;
  name: string;
  quote: string;
  layout: Layout;
};
type Founder = {
  kind: "founder";
  photo: StaticImageData;
  name: string;
  quote: string;
  reverse?: boolean;
  layout: Layout;
};
type DescImage = {
  kind: "desc-image";
  image: StaticImageData;
  desc: string;
  layout: Layout;
};
type Card = Testimonial | Founder | DescImage;

// Adjust left/top/width/height (rem) to tune the timeline stagger.
const CARDS: Card[] = [
  {
    kind: "testimonial",
    id: "01",
    name: "Marcus Thorne",
    quote:
      "I used to lose money on Fridays without ever knowing why, but the Dashboard turned my messy numbers into a clear picture that spotted my failures instantly.",
    layout: {
      left: 112,
      top: 11.25,
      width: 42.5,
      height: 46.125,
      mobileWidth: 39.5,
    },
  },
  {
    kind: "testimonial",
    id: "02",
    name: "Chloe Bennett",
    quote:
      "Spreadsheets were so boring that I always stopped journaling, but this visual Journal makes it easy to see my charts and finally learn from my past.",
    layout: {
      left: 144,
      top: 69.25,
      width: 46.625,
      height: 36.875,
      mobileWidth: 45.25,
    },
  },
  {
    kind: "founder",
    photo: flavioPhoto,
    name: "Flavio Schaffer",
    quote:
      "Our mission is to empower you to trade with discipline, not emotions. We were tired of losing to ourselves, so we built the tool that transforms emotional reactions into disciplined decisions.",
    layout: {
      left: 204,
      top: 2,
      width: 85.375,
      height: 102.25,
      mobileWidth: 41.25,
    },
    reverse: true,
  },
  {
    kind: "testimonial",
    id: "03",
    name: "Ryan O'Neil",
    quote:
      "Keeping track of my different accounts and prop challenges was a giant mess until I found this Accounts view that keeps everything organized and honest.",
    layout: {
      left: 308,
      top: 7.375,
      width: 42.5,
      height: 38.375,
      mobileWidth: 40.125,
    },
  },
  {
    kind: "testimonial",
    id: "04",
    name: "Maya Silva",
    quote:
      "I often got caught by surprise market news that blew up my trades, but the News Feed warns me before major events so I stay safe.",
    layout: {
      left: 340.62,
      top: 57.75,
      width: 47.625,
      height: 45.75,
      mobileWidth: 35,
    },
  },
  {
    kind: "desc-image",
    image: galaxyImage,
    desc: "Data spec_143.png",
    layout: {
      left: 384.25,
      top: 18,
      width: 28.125,
      height: 31.625,
      mobileWidth: 28.125,
    },
  },
  {
    kind: "testimonial",
    id: "05",
    name: "Sam Rivera",
    quote:
      "I used to keep my trading notes everywhere and lose my best ideas, but the Universal Notes tool keeps my screenshots and thoughts in one safe home.",
    layout: {
      left: 413.37,
      top: 72.88,
      width: 35.75,
      height: 45.75,
      mobileWidth: 41.125,
    },
  },
  {
    kind: "founder",
    photo: lionelPhoto,
    name: "Lionel Lüthi",
    quote:
      "Every painful mistake we made in the markets taught us one thing: consistency always beats intuition. We designed this platform to be the mirror every trader needs to turn chaotic habits into a repeatable, winning process.",
    layout: {
      left: 491.75,
      top: 7.625,
      width: 85.375,
      height: 102,
      mobileWidth: 41.25,
    },
  },
  {
    kind: "testimonial",
    id: "06",
    name: "Fiona Grace",
    quote: "Seeing my win rate and profit factor displayed helped me understand my trading edge.",
    layout: {
      left: 440.5,
      top: 14.75,
      width: 33.25,
      height: 35.375,
      mobileWidth: 38.25,
    },
  },
  {
    kind: "desc-image",
    image: galaxyImage2,
    desc: "Data spec_23.png",
    layout: {
      left: 680,
      top: 79,
      width: 20.375,
      height: 23.25,
      mobileWidth: 25,
    },
  },
  {
    kind: "testimonial",
    id: "07",
    name: "Tom Richards",
    quote: "Finding old trades took forever, but now I filter my Journal by setup or emotion to find answers fast.",
    layout: {
      left: 602.25,
      top: 75,
      width: 35.75,
      height: 35.375,
    },
  },

  {
    kind: "testimonial",
    id: "08",
    name: "Elena Kostas",
    quote:
      "My performance curve used to look fake because of deposits, but this tool separates my cash from my actual skill so I can see real growth.",
    layout: {
      left: 626.5,
      top: 19.625,
      width: 47.625,
      height: 40.875,
      mobileWidth: 41.875,
    },
  },
];

const TRACK_END_PADDING = 20; // rem
const TRACK_WIDTH = Math.max(...CARDS.map((c) => c.layout.left + c.layout.width)) + TRACK_END_PADDING;
const TRACK_HEIGHT = Math.max(...CARDS.map((c) => c.layout.top + c.layout.height));

function TestimonialCard({ card }: { card: Testimonial }) {
  const { id, name, quote } = card;
  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex w-fit shrink-0 items-center gap-[6rem] bg-[#0a0a0a] px-[1rem] py-[0.25rem] font-spacemono text-[1.75rem] leading-[1.4] whitespace-nowrap text-white uppercase">
        <span>{id}</span>
        <span>{name}</span>
      </div>
      <div className="min-h-0 flex-1 border border-[rgba(0,0,0,0.3)] p-[1rem]">
        <div className="flex h-full flex-col justify-between gap-[4rem] bg-[#f2f2f2] p-[3rem]">
          <svg
            className="w-[2.5rem] shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="17"
            viewBox="0 0 20 17"
            fill="none">
            <path
              d="M0 0H7.55102V7.31395C7.55102 8.63178 7.39229 9.88372 7.07483 11.0698C6.80272 12.2119 6.34921 13.2222 5.71429 14.1008C5.12472 14.9793 4.35374 15.6822 3.40136 16.2093C2.49433 16.7364 1.38322 17 0.0680277 17V13.7054C0.839003 13.7054 1.47392 13.5297 1.97279 13.1783C2.47166 12.7829 2.87982 12.2778 3.19728 11.6628C3.51474 11.0478 3.71882 10.3669 3.80952 9.62016C3.94558 8.82946 4.01361 8.06072 4.01361 7.31395H0V0ZM12.449 0H20V7.31395C20 8.63178 19.8413 9.88372 19.5238 11.0698C19.2517 12.2119 18.7982 13.2222 18.1633 14.1008C17.5737 14.9793 16.8027 15.6822 15.8503 16.2093C14.9433 16.7364 13.8322 17 12.517 17V13.7054C13.288 13.7054 13.9229 13.5297 14.4218 13.1783C14.9206 12.7829 15.3288 12.2778 15.6463 11.6628C15.9637 11.0478 16.1678 10.3669 16.2585 9.62016C16.3946 8.82946 16.4626 8.06072 16.4626 7.31395H12.449V0Z"
              fill="#FF5500"
            />
          </svg>
          <p className="mb-0 font-helveticaNeue text-[2.5rem] leading-[1.5] text-black">{quote}</p>
        </div>
      </div>
    </div>
  );
}

const FOUNDER_PHOTO_CROP: Crop = { w: 100, h: 100, left: 0, top: 0 };
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeReducedMotion(onChange: () => void) {
  const mql = window.matchMedia(REDUCED_MOTION_QUERY);
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

// Fills its parent. The real image stays mounted (hidden) so it is decoded before the ASCII handoff,
// and the ASCII canvas stays mounted underneath it so scrolling back reverses the reveal.
function FounderPhoto({ photo, alt, axis }: { photo: StaticImageData; alt: string; axis: "x" | "y" }) {
  const reduceMotion = React.useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false,
  );
  const [revealed, setRevealed] = React.useState(false);
  const showPhoto = revealed || reduceMotion;

  return (
    <div className="relative h-full w-full overflow-hidden bg-black">
      {!reduceMotion && (
        <AsciiReveal
          src={photo.src}
          crop={FOUNDER_PHOTO_CROP}
          axis={axis}
          onRevealedChange={setRevealed}
        />
      )}
      <NextImage
        src={photo}
        alt={alt}
        loading="eager"
        quality={100}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ visibility: showPhoto ? "visible" : "hidden" }}
      />
    </div>
  );
}

function FounderCard({ card }: { card: Founder }) {
  const { photo, name, quote, reverse } = card;
  return (
    <div className={`flex ${reverse ? "flex-col-reverse" : "flex-col"} w-full h-full gap-[2rem]`}>
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <FounderPhoto
          photo={photo}
          alt={name}
          axis="x"
        />
      </div>
      <div className="flex shrink-0 flex-col gap-[0.5rem]">
        <p className="max-w-[55rem] font-helveticaNeue text-[2.5rem] leading-[1.5] text-black">{quote}</p>
        <p className="font-takenByVultures text-[6rem] leading-[1.1] text-[#FF5500]">{name}</p>
      </div>
    </div>
  );
}

function DescImageCard({ card }: { card: DescImage }) {
  const { image, desc } = card;
  return (
    <div className="relative h-full w-full overflow-hidden">
      <p className="mb-[1rem] font-spacemono text-[1.75rem] leading-[1] text-black uppercase">{desc}</p>
      <Image
        src={image}
        alt=""
        className="object-cover"
        style={{ width: undefined, height: undefined }}
      />
    </div>
  );
}

function MobileTestimonialCard({ card }: { card: Testimonial }) {
  const { id, name, quote } = card;
  return (
    <div className="flex w-full flex-col">
      <div className="flex w-fit shrink-0 items-center gap-[6rem] bg-[#0a0a0a] px-[1rem] py-[0.25rem] font-spacemono text-[1.75rem] leading-[1.4] whitespace-nowrap text-white uppercase">
        <span>{id}</span>
        <span>{name}</span>
      </div>
      <div className="border border-[rgba(0,0,0,0.3)] p-[0.75rem]">
        <div className="flex flex-col gap-[2rem] bg-[#f2f2f2] p-[2rem]">
          <svg
            className="w-[2.5rem] shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="17"
            viewBox="0 0 20 17"
            fill="none">
            <path
              d="M0 0H7.55102V7.31395C7.55102 8.63178 7.39229 9.88372 7.07483 11.0698C6.80272 12.2119 6.34921 13.2222 5.71429 14.1008C5.12472 14.9793 4.35374 15.6822 3.40136 16.2093C2.49433 16.7364 1.38322 17 0.0680277 17V13.7054C0.839003 13.7054 1.47392 13.5297 1.97279 13.1783C2.47166 12.7829 2.87982 12.2778 3.19728 11.6628C3.51474 11.0478 3.71882 10.3669 3.80952 9.62016C3.94558 8.82946 4.01361 8.06072 4.01361 7.31395H0V0ZM12.449 0H20V7.31395C20 8.63178 19.8413 9.88372 19.5238 11.0698C19.2517 12.2119 18.7982 13.2222 18.1633 14.1008C17.5737 14.9793 16.8027 15.6822 15.8503 16.2093C14.9433 16.7364 13.8322 17 12.517 17V13.7054C13.288 13.7054 13.9229 13.5297 14.4218 13.1783C14.9206 12.7829 15.3288 12.2778 15.6463 11.6628C15.9637 11.0478 16.1678 10.3669 16.2585 9.62016C16.3946 8.82946 16.4626 8.06072 16.4626 7.31395H12.449V0Z"
              fill="#FF5500"
            />
          </svg>
          <p className="mb-0 font-helveticaNeue text-[2.25rem] leading-[1.5] text-black">{quote}</p>
        </div>
      </div>
    </div>
  );
}

function MobileFounderCard({ card }: { card: Founder }) {
  const { photo, name, quote, reverse } = card;
  return (
    <div className={`flex flex-col w-full gap-[2rem]`}>
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: `${photo.width} / ${photo.height}` }}>
        <FounderPhoto
          photo={photo}
          alt={name}
          axis="y"
        />
      </div>
      <div className="flex flex-col gap-[0.5rem]">
        <p className="font-helveticaNeue text-[2.25rem] leading-[1.5] text-black">{quote}</p>
        <p className="font-takenByVultures text-[5rem] leading-[1.1] text-[#FF5500]">{name}</p>
      </div>
    </div>
  );
}

function MobileDescImageCard({ card }: { card: DescImage }) {
  const { image, desc } = card;
  return (
    <div className="relative h-full w-full overflow-hidden">
      <p className="mb-[1rem] font-spacemono text-[1.75rem] leading-[1] text-black uppercase">{desc}</p>
      <Image
        src={image}
        alt=""
        className="object-cover"
        style={{ width: undefined, height: undefined }}
      />
    </div>
  );
}

function MobileSlider() {
  return (
    <div className="relative z-[1] flex flex-col gap-[4rem] px-[1.88rem] pb-[4rem]">
      {CARDS.map((card, i) => (
        <div
          key={i}
          style={{ width: `${card.layout.mobileWidth ?? 41}rem` }}
          className={`${i % 2 === 0 ? "mr-auto" : "ml-auto"}`}>
          {card.kind === "testimonial" ? (
            <MobileTestimonialCard card={card} />
          ) : card.kind === "founder" ? (
            <MobileFounderCard card={card} />
          ) : (
            <MobileDescImageCard card={card} />
          )}
        </div>
      ))}
    </div>
  );
}

function DesktopSlider() {
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const [sectionHeight, setSectionHeight] = React.useState("300vh");
  const [scrollRange, setScrollRange] = React.useState(0);

  const recalc = React.useCallback(() => {
    const vw = viewportRef.current?.clientWidth ?? window.innerWidth;
    const vh = window.innerHeight;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const trackPx = TRACK_WIDTH * rem;
    const range = Math.max(0, trackPx - vw);
    setScrollRange(range);
    setSectionHeight(`${vh + range}px`);
  }, []);

  React.useLayoutEffect(() => {
    recalc();
  }, [recalc]);

  React.useEffect(() => {
    const ro = new ResizeObserver(() => requestAnimationFrame(recalc));
    if (viewportRef.current) ro.observe(viewportRef.current);
    window.addEventListener("resize", recalc, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalc);
    };
  }, [recalc]);

  // "start 65%" = tracking begins when section top reaches 65% down the viewport,
  // so the user sees horizontal motion before the section is fully centered.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 65%", "end end"],
  });

  const xRaw = useTransform(scrollYProgress, [0, 1], [0, -scrollRange]);
  const x = useSpring(xRaw, { stiffness: 300, damping: 35, mass: 0.5 });

  return (
    <div
      ref={sectionRef}
      style={{ height: sectionHeight }}>
      <div
        ref={viewportRef}
        className="sticky top-0 h-screen overflow-hidden">
        <div className="absolute h-full w-full">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 100% 100%"
            fill="none"
            className="h-full w-full">
            <rect
              opacity="0.2"
              width="100%"
              height="100%"
              fill="url(#pattern0_1601_6376)"
            />
            <defs>
              <pattern
                id="pattern0_1601_6376"
                patternUnits="userSpaceOnUse"
                patternTransform="matrix(20 0 0 20 0 0)"
                preserveAspectRatio="none"
                viewBox="0 0 40 40"
                width="1"
                height="1">
                <g id="pattern0_1601_6376_inner">
                  <circle
                    cx="2"
                    cy="2"
                    r="2"
                    fill="black"
                  />
                </g>
              </pattern>
            </defs>
          </svg>
        </div>
        {/* Edge fade hints */}
        {/*<div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[8rem] bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[8rem] bg-gradient-to-l from-white to-transparent" />*/}

        <motion.div
          className="absolute will-change-transform"
          style={{
            x,
            top: "50%",
            marginTop: `-${TRACK_HEIGHT / 2}rem`,
            width: `${TRACK_WIDTH}rem`,
            height: `${TRACK_HEIGHT}rem`,
          }}>
          {CARDS.map((card, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${card.layout.left}rem`,
                top: `${card.layout.top}rem`,
                width: `${card.layout.width}rem`,
                height: `${card.layout.height}rem`,
              }}>
              {card.kind === "testimonial" ? (
                <TestimonialCard card={card} />
              ) : card.kind === "founder" ? (
                <FounderCard card={card} />
              ) : (
                <DescImageCard card={card} />
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

function TestimonialsSectionDesktop({ data }: { data: PageHomeSchema }) {
  return (
    <section
      className="relative hidden w-full overflow-x-clip lg:block"
      id="testimonials">
      <div className="flex flex-col items-center px-[4rem] text-center">
        {data?.testimonialsChip && (
          <motion.div
            className="mb-[2.5rem]"
            {...REVEAL_CHIP}
            transition={CHIP_TRANSITION}>
            <Chip
              text={data.testimonialsChip}
              animatedIcon
              spinnerSize={32}
              spinnerFps={4}
              spinnerVariant="down-up"
              className="[&_p]:!text-black prose-p:!text-black"
            />
          </motion.div>
        )}

        {data?.testimonialsTitle && (
          <motion.div
            className="mb-[4rem] max-w-[76.125rem] text-black prose-p:text-black/70"
            {...REVEAL_TITLE}
            transition={TITLE_TRANSITION}>
            <PortableText
              value={data.testimonialsTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}
      </div>
      <DesktopSlider />
    </section>
  );
}

function TestimonialsSectionMobile({ data }: { data: PageHomeSchema }) {
  return (
    <section
      className="relative w-full overflow-x-clip pt-[7rem] lg:hidden"
      id="testimonials">
      <div className="pointer-events-none absolute inset-0 z-0">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          width="430"
          height="3506"
          viewBox="0 0 430 3506"
          fill="none"
          className="h-full w-full"
          preserveAspectRatio="xMidYMid slice">
          <g opacity="0.2">
            <rect
              width="430"
              height="3602"
              fill="url(#mob-dots)"
            />
            <rect
              width="430"
              height="3602"
              fill="url(#mob-fade)"
            />
          </g>
          <defs>
            <linearGradient
              id="mob-fade"
              x1="215"
              y1="0.181641"
              x2="215"
              y2="307.682"
              gradientUnits="userSpaceOnUse">
              <stop stopColor="white" />
              <stop
                offset="1"
                stopColor="white"
                stopOpacity="0"
              />
            </linearGradient>
            <pattern
              id="mob-dots"
              patternUnits="userSpaceOnUse"
              patternTransform="matrix(21.336 0 0 21.336 0 0)"
              preserveAspectRatio="none"
              viewBox="0 0 30.48 30.48"
              width="1"
              height="1">
              <g>
                <circle
                  cx="2"
                  cy="2"
                  r="2"
                  fill="black"
                />
              </g>
            </pattern>
          </defs>
        </svg>
      </div>
      <div className="relative flex flex-col items-center px-[4rem] text-center">
        {data?.testimonialsChip && (
          <div className="mb-[2.5rem]">
            <Chip
              text={data.testimonialsChip}
              spinnerSize={32}
              spinnerFps={4}
              spinnerVariant="down-up"
              className="[&_p]:!text-black prose-p:!text-black"
            />
          </div>
        )}

        {data?.testimonialsTitle && (
          <div className="mb-[4rem] max-w-[76.125rem] text-black prose-p:text-black/70">
            <PortableText
              value={data.testimonialsTitle}
              components={richTextComponent}
            />
          </div>
        )}
      </div>
      <MobileSlider />
    </section>
  );
}

export default function TestimonialsSection({ data }: { data: PageHomeSchema }) {
  return (
    <>
      <TestimonialsSectionDesktop data={data} />
      <TestimonialsSectionMobile data={data} />
    </>
  );
}
